import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowDown, Check, Loader2, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIME = "#ff5a1f"; // TORQ signal orange — house accent, not borrowed from any reference site
const MAX_FILE = 5 * 1024 * 1024;

const ROLES = [
  "Station Judge",
  "Race Marshal",
  "Registration / Check-In Crew",
  "Finish-Line Official",
  "Event Operations Crew",
  "Any Available Role",
];

const CERTS = [
  "Certified Personal Trainer",
  "Certified Fitness Instructor",
  "Sports / Exercise Science Qualification",
  "Certified Functional Fitness Coach",
  "Other Fitness Certification",
  "No Certification",
];

const AVAILABILITY = ["Event Day", "Setup and Event Day", "Flexible"];

const EMPTY = {
  full_name: "", email: "", phone: "", city: "", role: "", certification: "",
  cert_name_org: "", experience: "", availability: "", reason: "", consent: false, website: "",
};

export default function OfficialsPage() {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const scrollToForm = (e) => {
    if (e) e.preventDefault();
    document.getElementById("official-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const okTypes = ["application/pdf", "image/jpeg", "image/png"];
    if (!okTypes.includes(f.type)) {
      toast.error("Certificate must be a PDF, JPG or PNG.");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_FILE) {
      toast.error("Certificate must be 5 MB or smaller.");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const missing = [];
    if (form.full_name.trim().length < 2) missing.push("full name");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) missing.push("a valid email");
    if (!/^[6-9]\d{9}$/.test(form.phone.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, ""))) missing.push("a valid 10-digit mobile");
    if (!form.city.trim()) missing.push("city");
    if (!form.role) missing.push("a preferred role");
    if (!form.certification) missing.push("a certification option");
    if (!form.availability) missing.push("availability");
    if (!form.consent) missing.push("privacy consent");
    if (missing.length) {
      const msg = `Before applying, add: ${missing.join(", ")}.`;
      setError(msg);
      toast.error(msg);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const body = new FormData();
      Object.entries(form).forEach(([k, v]) => body.append(k, String(v)));
      if (file) body.append("certificate", file);
      const res = await fetch(`${API}/officials/apply`, { method: "POST", body });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not submit your application");
      setDone(data);
      toast.success("Application received.");
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const darkLabel = "font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a] block mb-2";
  const darkField =
    "w-full bg-white border border-[#d8d8d0] text-[#161614] font-body text-base px-4 py-3 min-h-[48px] rounded-lg outline-none focus:border-[#161614] transition-colors placeholder:text-[#a3a39f]";

  return (
    <main className="min-h-screen bg-[#0c0c0a] text-[#f0eee4] font-body antialiased" data-testid="officials-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-12">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="officials-back" className="font-mono text-[11px] tracking-[0.25em] text-[#a3a39f] hover:text-[#f0eee4] transition-colors inline-flex items-center gap-2">
            <ArrowLeft size={14} /> BACK
          </Link>
          <img src="/logo-long.png" alt="TORQ Circuit" className="h-6 w-auto" />
        </div>

        <div className="mt-14 lg:mt-20 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          <div>
            <p className="font-mono text-[11px] tracking-[0.35em]" style={{ color: LIME }} data-testid="officials-overline">
              RACE TEAM — SEASON ONE
            </p>
            <h1
              className="mt-6 font-display font-extrabold uppercase leading-[0.88] text-[clamp(3rem,8vw,7rem)]"
              data-testid="officials-heading"
            >
              BECOME A <span style={{ color: LIME }}>TORQ OFFICIAL</span>
            </h1>
            <p className="mt-6 max-w-xl text-[#a3a39f] leading-relaxed text-base lg:text-lg" data-testid="officials-intro">
              Be part of the team that makes every race fair, safe, and unforgettable. Apply to join
              TORQ CIRCUIT as a judge, race marshal, or event crew member.
            </p>

            <ul className="mt-10 space-y-4" data-testid="officials-points">
              {[
                "Judge the seven stations and keep every rep honest.",
                "Marshal the running lanes and keep athletes safe.",
                "Run registration, check-in and the finish line.",
                "Certification helps — it is not required for every role.",
              ].map((b) => (
                <li key={b} className="flex items-start gap-4 text-sm lg:text-base text-[#a3a39f] leading-relaxed">
                  <span className="mt-1 shrink-0 w-4 h-4 flex items-center justify-center" style={{ color: LIME }}>
                    <Check size={14} strokeWidth={3} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>

            <button
              onClick={scrollToForm}
              data-testid="officials-cta"
              className="mt-10 inline-flex items-center gap-3 font-mono text-xs tracking-[0.25em] px-8 py-4 min-h-[52px] rounded-full font-bold text-[#0c0c0a] transition-transform hover:scale-[1.02] active:scale-[0.98]"
              style={{ background: LIME }}
            >
              APPLY NOW <ArrowDown size={14} />
            </button>
          </div>

          <div id="official-form" className="scroll-mt-8" data-testid="officials-form-panel">
            <div className="bg-[#f7f7f2] text-[#161614] rounded-2xl p-6 sm:p-10">
              {done ? (
                <div className="py-10 text-center" data-testid="official-success">
                  <span className="inline-flex items-center justify-center w-14 h-14 rounded-full border-2 border-[#161614]">
                    <Check size={24} strokeWidth={3} />
                  </span>
                  <h2 className="mt-6 font-display font-extrabold uppercase text-3xl">APPLICATION RECEIVED</h2>
                  <p className="mt-4 text-sm text-[#454540] leading-relaxed max-w-sm mx-auto">
                    Thank you for applying to join TORQ CIRCUIT. Our team will review your application and
                    contact you if you are selected.
                  </p>
                  <p className="mt-3 text-xs text-[#6e6e6a] leading-relaxed max-w-sm mx-auto">
                    Your application does not guarantee selection or an official assignment.
                  </p>
                  <p className="mt-4 font-mono text-[10px] tracking-[0.2em] text-[#6e6e6a]">REF — {done.reference}</p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="official-form-el">
                  <h2 className="font-display font-extrabold uppercase text-3xl sm:text-4xl leading-[0.9]" data-testid="official-form-title">
                    JOIN THE RACE TEAM
                  </h2>
                  <p className="mt-3 text-sm text-[#454540] leading-relaxed">
                    Tell us who you are and where you want to help. We review every application.
                  </p>

                  <div className="mt-8 grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="off-name" className={darkLabel}>FULL NAME *</label>
                      <input id="off-name" className={darkField} data-testid="official-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} maxLength={120} required />
                    </div>
                    <div>
                      <label htmlFor="off-email" className={darkLabel}>EMAIL ADDRESS *</label>
                      <input id="off-email" type="email" className={darkField} data-testid="official-email" value={form.email} onChange={(e) => set("email", e.target.value)} required />
                    </div>
                    <div>
                      <label htmlFor="off-phone" className={darkLabel}>MOBILE / WHATSAPP *</label>
                      <input id="off-phone" type="tel" className={darkField} data-testid="official-phone" placeholder="+91 98765 43210" value={form.phone} onChange={(e) => set("phone", e.target.value)} maxLength={20} required />
                    </div>
                    <div>
                      <label htmlFor="off-city" className={darkLabel}>CITY *</label>
                      <input id="off-city" className={darkField} data-testid="official-city" value={form.city} onChange={(e) => set("city", e.target.value)} maxLength={80} required />
                    </div>
                    <div>
                      <label htmlFor="off-role" className={darkLabel}>PREFERRED ROLE *</label>
                      <select id="off-role" className={darkField} data-testid="official-role" value={form.role} onChange={(e) => set("role", e.target.value)} required>
                        <option value="">SELECT</option>
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="off-cert" className={darkLabel}>FITNESS CERTIFICATION *</label>
                      <select id="off-cert" className={darkField} data-testid="official-cert" value={form.certification} onChange={(e) => set("certification", e.target.value)} required>
                        <option value="">SELECT</option>
                        {CERTS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="off-certorg" className={darkLabel}>CERTIFICATION NAME AND ISSUING ORGANISATION</label>
                      <input id="off-certorg" className={darkField} data-testid="official-certorg" value={form.cert_name_org} onChange={(e) => set("cert_name_org", e.target.value)} maxLength={200} />
                    </div>

                    <div className="sm:col-span-2">
                      <label className={darkLabel}>UPLOAD FITNESS CERTIFICATE (OPTIONAL — PDF, JPG, PNG, MAX 5 MB)</label>
                      <input
                        ref={fileRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={pickFile}
                        className="hidden"
                        data-testid="official-file-input"
                      />
                      {file ? (
                        <div className="flex items-center justify-between gap-4 bg-white border border-[#d8d8d0] rounded-lg px-4 py-3" data-testid="official-file-selected">
                          <span className="text-sm truncate">{file.name} <span className="text-[#6e6e6a]">({(file.size / 1024 / 1024).toFixed(1)} MB)</span></span>
                          <button type="button" onClick={() => { setFile(null); fileRef.current.value = ""; }} data-testid="official-file-remove" className="p-2 text-[#6e6e6a] hover:text-[#161614]" aria-label="Remove file">
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => fileRef.current.click()}
                          data-testid="official-file-btn"
                          className="w-full flex items-center justify-center gap-3 bg-white border border-dashed border-[#a3a39f] rounded-lg px-4 py-6 text-sm text-[#454540] hover:border-[#161614] transition-colors"
                        >
                          <UploadCloud size={18} /> CHOOSE FILE
                        </button>
                      )}
                      <p className="mt-2 font-mono text-[10px] tracking-[0.05em] text-[#6e6e6a] leading-relaxed">
                        Not required for every role — the organiser decides which assignments need certification. Files are private and only visible to the organising team.
                      </p>
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="off-exp" className={darkLabel}>RELEVANT FITNESS OR EVENT EXPERIENCE</label>
                      <input id="off-exp" className={darkField} data-testid="official-experience" value={form.experience} onChange={(e) => set("experience", e.target.value)} maxLength={2000} />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="off-avail" className={darkLabel}>AVAILABILITY *</label>
                      <select id="off-avail" className={darkField} data-testid="official-availability" value={form.availability} onChange={(e) => set("availability", e.target.value)} required>
                        <option value="">SELECT</option>
                        {AVAILABILITY.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="off-reason" className={darkLabel}>WHY DO YOU WANT TO JOIN TORQ CIRCUIT?</label>
                      <textarea id="off-reason" className={`${darkField} min-h-[110px]`} data-testid="official-reason" value={form.reason} onChange={(e) => set("reason", e.target.value)} maxLength={2000} />
                    </div>
                  </div>

                  <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                  <label className="mt-5 flex items-start gap-3 text-sm text-[#454540] leading-relaxed cursor-pointer" data-testid="official-consent-label">
                    <input
                      type="checkbox"
                      className="mt-1 w-4 h-4 shrink-0 accent-[#161614]"
                      checked={form.consent}
                      onChange={(e) => set("consent", e.target.checked)}
                      data-testid="official-consent"
                    />
                    <span>
                      I agree to be contacted by TORQ CIRCUIT about my application and have read the{" "}
                      <a href="/privacy-policy" target="_blank" rel="noreferrer" className="underline underline-offset-2 font-medium">Privacy Policy</a>.
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="official-submit"
                    className="mt-6 w-full rounded-full font-mono text-xs tracking-[0.25em] py-4 min-h-[52px] font-bold text-[#0c0c0a] transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60"
                    style={{ background: LIME }}
                  >
                    {busy ? (
                      <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> SUBMITTING…</span>
                    ) : (
                      "SUBMIT APPLICATION"
                    )}
                  </button>

                  {error && (
                    <p className="mt-4 font-mono text-[11px] tracking-[0.1em] text-[#8c2f2f] border border-[#8c2f2f]/40 rounded-lg px-4 py-3" data-testid="official-error">
                      {error}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
